import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/design-system/dropdown-menu';
import { IconButton } from '@/design-system/icon-button';
import { Paginator } from '@/design-system/pagination';
import { SearchField } from '@/design-system/search-field';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';
import {
  DeliveryListItem,
  DeliveryListParams,
  DownloadMode,
  deliveriesAPI,
} from '@/services/api/deliveries';
import { useAuth } from '@/contexts/AuthContext';
import { DeliveryStatusBadge, SlaPill } from './components/DeliveryStatusBadge';
import DeliveryFilterPanel, {
  DeliveryFilterValue,
  DEFAULT_BUSINESS_UNITS,
  DEFAULT_DELIVERY_STATUSES,
  EMPTY_DELIVERY_FILTERS,
  STATUS_FILTERS,
  countActiveDeliveryFilters,
} from './components/DeliveryFilterPanel';
import {
  useUrlNumber,
  useUrlPatch,
  useUrlSearch,
  parseArray,
  arraysEqual,
} from '@/hooks/urlState';
import type { DeliveryStatus } from '@/services/api/deliveries';
import {
  useDeliverySelection,
  type SelectionGroup,
} from './components/useDeliverySelection';
import BulkActionBar from './components/BulkActionBar';
import BulkConfirmModal from './components/BulkConfirmModal';
import BulkReasonModal from './components/BulkReasonModal';

const DEFAULT_PAGE_SIZE = 10;

// URL param keys for the applied filter object (draft stays in local state).
const FILTER_PARAMS = {
  status: 'status',
  cod: 'cod',
  businessUnit: 'bu',
  clientID: 'client',
  providerOutletIDs: 'outlet',
  driverID: 'driver',
  startDate: 'start',
  endDate: 'end',
  scheduleStartDate: 'schedStart',
  scheduleEndDate: 'schedEnd',
} as const;

// ── Format helpers ────────────────────────────────────────────────────
const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${date} ${time}`;
};

// Schedule date renders relative when future: "D+3 (12/06/2026 09:30)".
// Past schedules just show the formatted date — the relative half loses
// meaning once the pickup window has closed.
const formatScheduleDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const dayDiff = Math.round(
    (d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );
  const formatted = formatDateTime(iso);
  return dayDiff < 0 ? formatted : `D+${dayDiff} (${formatted})`;
};

const formatCurrency = (amount: number | null | undefined): string => {
  if (amount == null) return '—';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const DeliveryPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Search / filters / pagination are URL-driven (shareable, back-button
  // aware). No tab on this page — status is a filter field. Filter/search
  // changes drop `page` in the same URL write (no double-fetch race).
  // URL-backed search; the box resyncs on external URL changes (Back/forward,
  // programmatic jumps). PRD §7.5 — 1000ms debounce before it hits the URL.
  const {
    value: search,
    setValue: setSearch,
    committed: debouncedSearch,
  } = useUrlSearch('q', { resetPage: true, delay: 1000 });

  // Applied filters derived from the URL; draft stays local for the popover.
  const { searchParams, patch: patchUrl } = useUrlPatch();
  const filters: DeliveryFilterValue = useMemo(() => {
    const bu = searchParams.get(FILTER_PARAMS.businessUnit);
    return {
      status: parseArray<DeliveryStatus>(
        searchParams.get(FILTER_PARAMS.status),
        STATUS_FILTERS
      ),
      codPaymentMethod: parseArray(searchParams.get(FILTER_PARAMS.cod)),
      // Absent → the default scope; present (even empty) → what the user chose.
      businessUnit:
        bu == null ? [...DEFAULT_BUSINESS_UNITS] : parseArray(bu),
      clientID: searchParams.get(FILTER_PARAMS.clientID) ?? '',
      providerOutletIDs: searchParams.get(FILTER_PARAMS.providerOutletIDs) ?? '',
      driverID: searchParams.get(FILTER_PARAMS.driverID) ?? '',
      startDate: searchParams.get(FILTER_PARAMS.startDate) ?? '',
      endDate: searchParams.get(FILTER_PARAMS.endDate) ?? '',
      scheduleStartDate: searchParams.get(FILTER_PARAMS.scheduleStartDate) ?? '',
      scheduleEndDate: searchParams.get(FILTER_PARAMS.scheduleEndDate) ?? '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);
  const [filterDraft, setFilterDraft] = useState<DeliveryFilterValue>(
    EMPTY_DELIVERY_FILTERS
  );
  const [filterOpen, setFilterOpen] = useState(false);

  const [items, setItems] = useState<DeliveryListItem[]>([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [page, setPage] = useUrlNumber('page', 1, { min: 1 });
  const [pageSize, setPageSize] = useUrlNumber('pageSize', DEFAULT_PAGE_SIZE, {
    min: 1,
    resetPage: true,
  });
  const [total, setTotal] = useState(0);

  const [isDownloading, setIsDownloading] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);

  // ── Bulk selection state (PRD §7.7) ──────────────────────────────────
  const selection = useDeliverySelection({ items });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [activeModal, setActiveModal] = useState<
    null | 'allocate' | 'returned' | 'failed-return' | 'complete' | 'failed'
  >(null);

  // Clear selection whenever the visible result set changes — keeps the
  // bulk bar from acting on rows the user can no longer see.
  useEffect(() => {
    selection.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, filters]);

  const fetchList = useCallback(async () => {
    setTableLoading(true);
    const params: DeliveryListParams = {
      page,
      size: pageSize,
    };
    if (debouncedSearch) params.search = debouncedSearch;
    // Always scope by status: use the user's selection when present, otherwise
    // fall back to the full default status set so opening the list is scoped.
    params.status = (
      filters.status.length > 0 ? filters.status : DEFAULT_DELIVERY_STATUSES
    ).join(',');
    if (filters.codPaymentMethod.length > 0)
      params.codPaymentMethod = filters.codPaymentMethod.join(',');
    // Always scope to the default business units when nothing is selected so
    // the list never returns the units we've intentionally excluded.
    params.businessUnit = (
      filters.businessUnit.length > 0
        ? filters.businessUnit
        : DEFAULT_BUSINESS_UNITS
    ).join(',');
    if (filters.clientID) params.providerIDs = filters.clientID;
    if (filters.providerOutletIDs)
      params.providerOutletIDs = filters.providerOutletIDs;
    if (filters.driverID) params.driverID = filters.driverID;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.scheduleStartDate)
      params.scheduleStartDate = filters.scheduleStartDate;
    if (filters.scheduleEndDate)
      params.scheduleEndDate = filters.scheduleEndDate;

    try {
      const data = await deliveriesAPI.list(params);
      setItems(data.data ?? []);
      setTotal(data.pagination?.total ?? 0);
    } catch (err) {
      setItems([]);
      setTotal(0);
      toast.error('Could not load deliveries', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setTableLoading(false);
    }
  }, [page, pageSize, debouncedSearch, filters]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // `/` focuses the search box — same affordance as the invoices page.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inEditable =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable;
      if (inEditable) return;
      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const activeFilterCount = useMemo(
    () => countActiveDeliveryFilters(filters),
    [filters]
  );

  const handleFilterOpenChange = (open: boolean) => {
    if (open) setFilterDraft(filters);
    setFilterOpen(open);
  };

  // Write every filter field + drop `page` in ONE URL patch (atomic — one
  // fetch, no stale-page race). businessUnit is omitted when it equals the
  // default scope so the URL stays clean.
  const writeFilters = (v: DeliveryFilterValue) => {
    patchUrl((params) => {
      const setArr = (key: string, arr: string[]) => {
        if (arr.length > 0) params.set(key, arr.join(','));
        else params.delete(key);
      };
      const setStr = (key: string, value: string) => {
        if (value) params.set(key, value);
        else params.delete(key);
      };
      setArr(FILTER_PARAMS.status, v.status);
      setArr(FILTER_PARAMS.cod, v.codPaymentMethod);
      if (arraysEqual(v.businessUnit, DEFAULT_BUSINESS_UNITS)) {
        params.delete(FILTER_PARAMS.businessUnit);
      } else {
        // Empty string distinguishes "user cleared all" from "absent = default".
        params.set(FILTER_PARAMS.businessUnit, v.businessUnit.join(','));
      }
      setStr(FILTER_PARAMS.clientID, v.clientID);
      setStr(FILTER_PARAMS.providerOutletIDs, v.providerOutletIDs);
      setStr(FILTER_PARAMS.driverID, v.driverID);
      setStr(FILTER_PARAMS.startDate, v.startDate);
      setStr(FILTER_PARAMS.endDate, v.endDate);
      setStr(FILTER_PARAMS.scheduleStartDate, v.scheduleStartDate);
      setStr(FILTER_PARAMS.scheduleEndDate, v.scheduleEndDate);
      params.delete('page');
    });
  };

  const applyFilter = () => {
    writeFilters(filterDraft);
    setFilterOpen(false);
  };
  const resetFilter = () => {
    setFilterDraft(EMPTY_DELIVERY_FILTERS);
    writeFilters(EMPTY_DELIVERY_FILTERS);
    setFilterOpen(false);
  };

  /** Max default range when the user hasn't picked a date filter.
   *  Mirrors backoffice's `maxDeliveryCreatedDateRange = 32` (days). */
  const MAX_DEFAULT_RANGE_DAYS = 32;

  /** Local-timezone YYYY-MM-DD formatter — same as backoffice's
   *  `formatLocalDate`. NEVER use `toISOString().slice(0, 10)` here:
   *  that returns the UTC calendar date which can be ahead of the
   *  user's day after 17:00 WIB. */
  const formatLocalDate = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const handleDownload = useCallback(
    async (mode: DownloadMode) => {
      if (!filters.clientID) return;
      setIsDownloading(true);

      const today = new Date();
      const defaultStart = new Date(today);
      defaultStart.setDate(today.getDate() - MAX_DEFAULT_RANGE_DAYS);

      const start_date = filters.startDate || formatLocalDate(defaultStart);
      const end_date = filters.endDate || formatLocalDate(today);

      try {
        const result = await deliveriesAPI.download({
          providerID: filters.clientID,
          mode,
          email: user?.email ?? '',
          start_date,
          end_date,
          ...(filters.status.length > 0
            ? { status: filters.status.join(',') }
            : {}),
          // Outlet — send both casings, mirroring backoffice. The backend
          // accepts one of them and silently ignores the other; we don't
          // know which is canonical so we hedge.
          ...(filters.providerOutletIDs
            ? {
                providerOutletIDs: filters.providerOutletIDs,
                providerOutletIds: filters.providerOutletIDs,
              }
            : {}),
          // Driver — singular `driverID`, matching backoffice's
          // download-endpoint convention (NOT `drivers` like the list).
          ...(filters.driverID ? { driverID: filters.driverID } : {}),
          businessUnit: (filters.businessUnit.length > 0
            ? filters.businessUnit
            : DEFAULT_BUSINESS_UNITS
          ).join(','),
        });

        if (result.isFile) {
          // The blob download fired silently inside the service. Give
          // the user explicit confirmation — some browsers don't show
          // a download indicator when the destination is preconfigured.
          toast.success('Download started', {
            description: 'Your file is being saved to your downloads folder.',
          });
        } else {
          toast.success('Download queued', {
            description:
              result.message ?? 'Your file will be sent to your email shortly.',
          });
        }
      } catch (err) {
        toast.error('Download failed', {
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      } finally {
        setIsDownloading(false);
      }
    },
    [filters, user]
  );

  const canDownload = Boolean(filters.clientID);

  // ── Bulk action handlers ─────────────────────────────────────────────
  const afterBulkSuccess = useCallback(
    (message: string) => {
      toast.success('Success', { description: message });
      selection.clear();
      setActiveModal(null);
      fetchList();
    },
    [selection, fetchList]
  );

  const handleBulkError = (err: unknown) => {
    toast.error('Bulk action failed', {
      description: err instanceof Error ? err.message : 'Server error',
    });
  };

  const runBulkAllocate = async () => {
    setBulkBusy(true);
    try {
      const res = await deliveriesAPI.bulkAllocate(selection.selectedKeys);
      afterBulkSuccess(res?.message ?? 'Packages are being processed.');
    } catch (err) {
      handleBulkError(err);
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkUpdate = async (
    status: 'RETURNED' | 'FAILED_IN_RETURN' | 'COMPLETED' | 'FAILED',
    notes?: string,
    reasonCode?: string
  ) => {
    setBulkBusy(true);
    try {
      const payload = selection.selectedKeys.map((deliveryUID) => ({
        deliveryUID,
        status,
        ...(notes ? { notes } : {}),
        ...(reasonCode ? { reasonCode } : {}),
      }));
      const res = await deliveriesAPI.bulkUpdate(payload);
      afterBulkSuccess(res?.message ?? 'Delivery status updated.');
    } catch (err) {
      handleBulkError(err);
    } finally {
      setBulkBusy(false);
    }
  };

  // Map the active group to the `state` filter expected by the reasons
  // API for the COMPLETION / FAILED modals.
  const reasonStateFor = (
    group: SelectionGroup | null
  ): 'PRE_PICKUP' | 'POST_PICKUP' | 'IN_RETURN' | 'UNCATEGORIZED' => {
    if (group === 'PRE_PICKUP') return 'PRE_PICKUP';
    if (group === 'POST_PICKUP') return 'POST_PICKUP';
    if (group === 'IN_RETURN') return 'IN_RETURN';
    return 'UNCATEGORIZED';
  };

  // ── Column registers — match invoices page typography conventions ──
  const codeCls =
    'font-mono text-[12px] font-semibold text-text-strong-950 tabular-nums';
  const dateCls = 'text-[13px] text-text-sub-600 tabular-nums';
  const nameCls = 'text-[13px] text-text-strong-950 tracking-tight';
  const metaCls = 'text-[11px] text-text-sub-600';

  const columns: TableDataColumn<DeliveryListItem>[] = [
    {
      key: 'deliveryID',
      title: 'Delivery ID',
      render: (_v, record) => (
        <span
          className={`${codeCls} cursor-pointer text-accent hover:underline`}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/delivery/${record.deliveryID}`);
          }}
        >
          {record.deliveryID}
        </span>
      ),
    },
    {
      key: 'createdAt',
      title: 'Created',
      render: (_v, record) => (
        <span className={dateCls}>{formatDateTime(record.createdAt)}</span>
      ),
    },
    {
      key: 'client',
      title: 'Client / outlet',
      render: (_v, record) => (
        <div>
          <p className={nameCls}>{record.provider?.name ?? '—'}</p>
          <p className={metaCls}>{record.providerOutlet?.name ?? '—'}</p>
        </div>
      ),
    },
    {
      key: 'status',
      title: 'State',
      render: (_v, record) => <DeliveryStatusBadge status={record.status} />,
    },
    {
      key: 'driver',
      title: 'Driver',
      render: (_v, record) => {
        const name = record.courier?.name ?? '';
        const initial = name ? name.charAt(0).toUpperCase() : '·';
        return (
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-bg-weak-50 text-[11px] font-semibold text-text-sub-600">
              {initial}
            </div>
            <span className={nameCls}>{name || '—'}</span>
          </div>
        );
      },
    },
    {
      key: 'cod',
      title: 'COD',
      render: (_v, record) => {
        if (!record.cashOnDelivery) {
          return <span className="text-[12px] text-text-sub-600">—</span>;
        }
        return (
          <div>
            <p className={`${nameCls} tabular-nums`}>
              {formatCurrency(record.cashOnDelivery.amount)}
            </p>
            <p className={metaCls}>
              {record.cashOnDelivery.paymentMethod?.replace(/_/g, ' ')}
            </p>
          </div>
        );
      },
    },
    {
      key: 'recipient',
      title: 'Recipient',
      render: (_v, record) => (
        <span className={nameCls}>{record.recipient?.firstName ?? '—'}</span>
      ),
    },
    {
      key: 'schedule',
      title: 'Schedule',
      render: (_v, record) => (
        <span className={dateCls}>
          {formatScheduleDate(record.schedule?.pickupTimeFrom) ?? '-'}
        </span>
      ),
    },
    {
      key: 'pickupAt',
      title: 'Picked up',
      render: (_v, record) => (
        <span className={dateCls}>
          {formatDateTime(record.pickupAt) ?? '-'}
        </span>
      ),
    },
    {
      key: 'completedAt',
      title: 'Completed',
      render: (_v, record) => (
        <span className={dateCls}>
          {formatDateTime(record.completedAt) ?? '-'}
        </span>
      ),
    },
    {
      key: 'pickupSla',
      title: 'Pickup SLA',
      render: (_v, record) => <SlaPill status={record.pickup_sla_status} />,
    },
    {
      key: 'completeSla',
      title: 'Delivery SLA',
      render: (_v, record) => <SlaPill status={record.complete_sla_status} />,
    },
    {
      key: 'businessUnit',
      title: 'Business unit',
      render: (_v, record) => (
        <span className={metaCls}>
          {record.businessUnit?.replace(/_/g, ' ') ?? '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (_v, record) => (
        <IconButton
          type="button"
          tone="neutral"
          style="ghost"
          size="sm"
          aria-label={`View delivery ${record.deliveryID}`}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/delivery/${record.deliveryID}`);
          }}
        >
          <i className="ri-eye-line text-[16px]" />
        </IconButton>
      ),
    },
  ];

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-[28px] font-extrabold tracking-tight text-text-strong-950 leading-none">
            Delivery
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-text-sub-600 leading-relaxed">
            Monitor every delivery from pickup to drop-off. Filter by client,
            status, COD, or schedule window to find the package you need.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button tone="neutral" style="stroke" onClick={fetchList}>
            <i className="ri-refresh-line mr-1.5 text-[14px]" />
            Refresh
          </Button>

          {/* Download — enabled only when a client is selected */}
          <div title={canDownload ? undefined : 'Pilih 1 Client Untuk Download'}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild disabled={!canDownload || isDownloading}>
                <Button
                  tone="primary"
                  style="stroke"
                  disabled={!canDownload || isDownloading}
                  loading={isDownloading}
                >
                  {!isDownloading && (
                    <i className="ri-download-2-line mr-1.5 text-[14px]" />
                  )}
                  Download
                  <i className="ri-arrow-down-s-line ml-1.5 text-[14px]" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onSelect={() => handleDownload('BILLING')}>
                  <i className="ri-file-chart-line text-accent" />
                  <div>
                    <p className="font-semibold text-text-strong-950">Billing</p>
                    <p className="text-[11px] text-text-sub-600">Finance</p>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => handleDownload('TRACKING')}>
                  <i className="ri-map-pin-2-line text-accent" />
                  <div>
                    <p className="font-semibold text-text-strong-950">
                      Tracking
                    </p>
                    <p className="text-[11px] text-text-sub-600">Operations</p>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <Card padding="none">
        <div className="flex flex-col gap-3 border-b border-stroke-soft-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="text-[12px] tabular text-text-sub-600">
            <span className="font-semibold text-text-strong-950">{total}</span>{' '}
            {total === 1 ? 'Delivery' : 'Deliveries'}
          </div>

          <div className="flex flex-1 items-center justify-end gap-2">
            <SearchField
              ref={searchInputRef}
              value={search}
              onChange={setSearch}
              placeholder="Search delivery ID, recipient, driver…"
              size="sm"
              className="max-w-xs flex-1"
            />
            <DeliveryFilterPanel
              open={filterOpen}
              onOpenChange={handleFilterOpenChange}
              activeFilterCount={activeFilterCount}
              value={filterDraft}
              onChange={setFilterDraft}
              onApply={applyFilter}
              onReset={resetFilter}
            />
          </div>
        </div>

        <div className="p-4">
          <TableData<DeliveryListItem>
            data={items}
            columns={columns}
            loading={tableLoading}
            size="sm"
            onRowClick={(record) => navigate(`/delivery/${record.deliveryID}`)}
            selection={{
              selectedKeys: selection.selectedKeys,
              onSelectionChange: selection.setSelectedKeys,
              getRowKey: (record) => record.deliveryID,
              isRowSelectable: selection.isRowSelectable,
              headerCheckboxDisabled: selection.pageSpansMultipleGroups,
            }}
          />

          {total > 0 && (
            <Paginator
              currentPage={page}
              totalItems={total}
              itemsPerPage={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[10, 20, 30, 50]}
            />
          )}
        </div>
      </Card>

      {/* ── Bulk action bar (floating, fixed bottom) ───────────────── */}
      <BulkActionBar
        count={selection.selectedKeys.length}
        activeGroup={selection.activeGroup}
        busy={bulkBusy}
        onClear={selection.clear}
        onAllocate={() => setActiveModal('allocate')}
        onMarkReturned={() => setActiveModal('returned')}
        onMarkFailedReturn={() => setActiveModal('failed-return')}
        onMarkComplete={() => setActiveModal('complete')}
        onMarkFailed={() => setActiveModal('failed')}
      />

      {/* ── Bulk modals ────────────────────────────────────────────── */}
      <BulkConfirmModal
        isOpen={activeModal === 'allocate'}
        onClose={() => setActiveModal(null)}
        onConfirm={runBulkAllocate}
        isLoading={bulkBusy}
        count={selection.selectedKeys.length}
        actionLabel="Allocate"
        title="Process selected packages?"
        description="Selected packages will be moved into the allocating stage."
        confirmLabel="Yes, Process"
        icon="ri-flashlight-line"
        iconTone="primary"
      />

      <BulkConfirmModal
        isOpen={activeModal === 'returned'}
        onClose={() => setActiveModal(null)}
        onConfirm={() => runBulkUpdate('RETURNED')}
        isLoading={bulkBusy}
        count={selection.selectedKeys.length}
        actionLabel="Mark as Returned"
        title="Mark packages as Returned?"
        description="Selected packages will be marked as returned to the origin hub."
        confirmLabel="Yes, Mark"
        icon="ri-archive-2-line"
        iconTone="primary"
      />

      <BulkReasonModal
        isOpen={activeModal === 'failed-return'}
        onClose={() => setActiveModal(null)}
        onConfirm={(notes, reasonCode) =>
          runBulkUpdate('FAILED_IN_RETURN', notes, reasonCode)
        }
        isLoading={bulkBusy}
        count={selection.selectedKeys.length}
        title="Confirm Bulk Mark as Failed Return?"
        description="Mark packages as failed to return to the hub."
        confirmLabel="Mark as Failed Return"
        state="IN_RETURN"
        type="FAILED"
        icon="ri-close-circle-line"
        iconTone="danger"
        variant="danger"
      />

      <BulkReasonModal
        isOpen={activeModal === 'complete'}
        onClose={() => setActiveModal(null)}
        onConfirm={(notes, reasonCode) =>
          runBulkUpdate('COMPLETED', notes, reasonCode)
        }
        isLoading={bulkBusy}
        count={selection.selectedKeys.length}
        title="Confirm Bulk Mark as Complete Delivery?"
        description="Mark packages as successfully completed."
        confirmLabel="Mark as Complete"
        state={reasonStateFor(selection.activeGroup)}
        type="COMPLETION"
        icon="ri-checkbox-circle-line"
        iconTone="success"
      />

      <BulkReasonModal
        isOpen={activeModal === 'failed'}
        onClose={() => setActiveModal(null)}
        onConfirm={(notes, reasonCode) =>
          runBulkUpdate('FAILED', notes, reasonCode)
        }
        isLoading={bulkBusy}
        count={selection.selectedKeys.length}
        title="Confirm Bulk Mark as Failed Delivery?"
        description="Mark packages as failed to deliver."
        confirmLabel="Mark as Failed"
        state={reasonStateFor(selection.activeGroup)}
        type="FAILED"
        icon="ri-close-circle-line"
        iconTone="danger"
        variant="danger"
      />
    </div>
  );
};

export default DeliveryPage;
